import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderGit2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchAllKnowns, type CtoRepoKnownRow } from '../../lib/ctoKnowns';
import {
  fetchAllReviews,
  subscribeCtoReviewsLive,
  type CtoReviewRow,
} from '../../lib/ctoReviews';
import { CtoRepoList } from './CtoRepoList';

/**
 * Remotes we have already seen — a review run or a stored known. Stacked so a
 * later GitHub install can attach to a known subject instead of inventing a
 * second thread.
 */

export default function CtoReposView() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<CtoReviewRow[]>([]);
  const [knowns, setKnowns] = useState<CtoRepoKnownRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, stored] = await Promise.all([fetchAllReviews(), fetchAllKnowns()]);
      setReviews(rows);
      setKnowns(stored);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load repositories');
      setReviews([]);
      setKnowns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [rows, stored] = await Promise.all([fetchAllReviews(), fetchAllKnowns()]);
      setReviews(rows);
      setKnowns(stored);
    } catch {
      /* Live refresh */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => subscribeCtoReviewsLive(() => void refresh()), [refresh]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FolderGit2 className="h-7 w-7 text-fuchsia-500" />
          Codebases
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-3xl">
          Codebases that have shown up in a review or a known. Linking them is a
          stub until we can actually open the repository from here.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-24">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : (
        <CtoRepoList
          reviews={reviews}
          knowns={knowns}
          onOpenReviews={() => navigate('/cto/reviews')}
        />
      )}
    </div>
  );
}
